import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useSignInMutation, useSignUpMutation } from '@entities/User';
import { trackEvent } from '@shared/analytics';
import { LANDING_URL, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, Divider, Field, Input, PasswordInput } from '@shared/ui';
import { authErrorField, mapAuthError } from '../model/errorMap';
import { SocialButtons } from './SocialButtons';

interface AuthFormProps {
  mode: 'signin' | 'signup';
  /** Пришёл за игрой (`?next=/stories…`): подзаголовок регистрации — про игру. */
  fromGame?: boolean;
  /** Пришёл из демо игры (`?via=demo`): без имени — почта, пароль и галочка (решение владельца 2026-10-30). */
  light?: boolean;
}

// Регистрация — имя, почта, пароль и галочка (2026-10-27): повтор пароля убран — у пароля есть «глазок»;
// кнопка всегда активна, без галочки подсказка встаёт под ней. Из 11 открывших форму с лендинга
// регистрировались 3 (Amplitude, 16–26.10).
type FieldErrors = Partial<Record<'name' | 'email' | 'password' | 'offer', string>>;

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Публичные документы живут на лендинге (lomora.ru) — доступны без логина,
// чтобы их мог открыть и пользователь, и ревьюер платёжной системы.
const OFFER_URL = `${LANDING_URL}/offer`;
const PRIVACY_URL = `${LANDING_URL}/privacy`;

// Плавный морфинг формы при переключении вход↔регистрация (форма остаётся
// смонтированной — /auth и /signup рендерят один и тот же AuthForm). Лишние поля
// появляются/сворачиваются с fade (popLayout убирает их из потока на выходе, чтобы
// соседи не «прыгали»), а сдвигающиеся поля плавно переезжают через layout.
const collapseField = {
  layout: true as const,
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2, ease: 'easeInOut' as const },
};
const layoutTransition = { duration: 0.26, ease: 'easeInOut' as const };

export function AuthForm({ mode, fromGame = false, light = false }: AuthFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const signIn = useSignInMutation();
  const signUp = useSignUpMutation();
  const isSignUp = mode === 'signup';
  const askName = isSignUp && !light;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedOffer, setAcceptedOffer] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  // Где уходят с регистрации (владелец 2026-10-30: из Instagram ушли с формы за 5 секунд, не тронув ни одного поля):
  // открыл → начал вводить (какое поле) → нажал «Создать» → отказ формы или сервера; успех — цель `registration`.
  // Только имена полей и коды ошибок, без введённого.
  const via = light ? 'demo' : 'direct';
  const started = useRef(new Set<string>());
  useEffect(() => {
    if (isSignUp) trackEvent('signup_form_viewed', { via });
  }, [isSignUp, via]);
  const noteStart = (field: 'name' | 'email' | 'password') => {
    if (!isSignUp || started.current.has(field)) return;
    started.current.add(field);
    trackEvent('signup_field_started', { via, field });
  };

  const activeMutation = isSignUp ? signUp : signIn;
  const isPending = activeMutation.isPending;
  const backendError = activeMutation.error ? t(mapAuthError(activeMutation.error)) : null;
  // Сервер отверг конкретное поле — ошибка под ним, плашка только для остального (занято, сбой, нет связи).
  const backendField = authErrorField(activeMutation.error);
  const fieldError = (field: 'name' | 'email' | 'password') =>
    errors[field] ?? (backendField === field ? (backendError ?? undefined) : undefined);

  const clearError = (field: keyof FieldErrors) => {
    if (backendField === field) activeMutation.reset();
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (askName && !name.trim()) e.name = t('auth.errors.nameRequired');
    if (!email.trim()) e.email = t('auth.errors.emailRequired');
    else if (!isValidEmail(email)) e.email = t('auth.errors.invalidEmail');
    if (!password) e.password = t('auth.errors.passwordRequired');
    else if (password.length < 8) e.password = t('auth.errors.shortPassword');
    if (isSignUp && !acceptedOffer) e.offer = t('auth.errors.offerRequired');
    return e;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isPending) return;
    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      if (isSignUp) trackEvent('signup_rejected', { via, source: 'form', fields: Object.keys(v) });
      return;
    }
    setErrors({});
    activeMutation.reset();
    if (isSignUp) {
      trackEvent('signup_submitted', { via });
      signUp.mutate(
        { ...(askName ? { name: name.trim() } : {}), email: email.trim(), password },
        { onError: (err) => trackEvent('signup_rejected', { via, source: 'server', reason: mapAuthError(err) }) },
      );
    } else {
      signIn.mutate({ email: email.trim(), password });
    }
  };

  const switchMode = () => {
    activeMutation.reset();
    setErrors({});
    navigate(isSignUp ? paths.auth : paths.signUp);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="my-auto flex w-full max-w-[360px] flex-col gap-3.5">
      {/* До 360 px — 32 px: «возвращением» в 40 px (296 px) не помещалось в карточку 262–272 px и давало прокрутку вбок
          (приёмка A1-15); в 32 px слово ≈ 237 px. */}
      {/* Низкий экран телефона — заголовок в одну строку, без подзаголовка: кнопка «Создать» видна без прокрутки. */}
      <h1 className="mb-1 font-serif text-[32px] font-medium leading-tight tracking-[-0.02em] min-[360px]:text-[40px] [@media(max-width:899px)_and_(max-height:720px)]:text-[32px]">
        {t(isSignUp ? 'auth.signUp.title' : 'auth.signIn.title')}
      </h1>
      <div className="mb-3 text-[14px] text-muted [@media(max-width:899px)_and_(max-height:720px)]:hidden">
        {t(isSignUp ? (fromGame ? 'auth.signUp.subGame' : 'auth.signUp.sub') : 'auth.signIn.sub')}
      </div>

      <SocialButtons onStart={isSignUp ? () => trackEvent('signup_yandex_clicked', { via }) : undefined} />
      <Divider>{t('auth.social.orEmail')}</Divider>

      <AnimatePresence initial={false} mode="popLayout">
        {askName && (
          <motion.div key="name" {...collapseField}>
            <Field label={t('auth.fields.name')} error={fieldError('name')}>
              {/* amp-mask / ym-disable-keys: ввод имени и email не попадает в записи сессий. */}
              <Input
                type="text"
                className="amp-mask ym-disable-keys"
                placeholder={t('auth.fields.namePlaceholder')}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearError('name');
                  noteStart('name');
                }}
                autoComplete="name"
                maxLength={50}
                disabled={isPending}
                invalid={!!fieldError('name')}
              />
            </Field>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div layout transition={layoutTransition}>
        <Field label={t('auth.fields.email')} error={fieldError('email')}>
          <Input
            type="email"
            className="amp-mask ym-disable-keys"
            placeholder={t('auth.fields.emailPlaceholder')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearError('email');
              noteStart('email');
            }}
            autoComplete={isSignUp ? 'email' : 'username'}
            disabled={isPending}
            invalid={!!fieldError('email')}
          />
        </Field>
      </motion.div>

      <motion.div layout transition={layoutTransition}>
        <Field label={t('auth.fields.password')} error={fieldError('password')}>
          <PasswordInput
            placeholder={t('auth.fields.passwordPlaceholder')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearError('password');
              noteStart('password');
            }}
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            disabled={isPending}
            invalid={!!fieldError('password')}
          />
        </Field>
      </motion.div>

      <AnimatePresence initial={false} mode="popLayout">
        {!isSignUp && (
          <motion.div key="forgot" {...collapseField} className="-mt-2 flex justify-end">
            <Link
              to={paths.forgotPassword}
              className="text-[12px] font-medium text-muted transition-colors hover:text-accent"
            >
              {t('auth.forgotLink')}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {isSignUp && (
          <motion.label
            key="offer"
            {...collapseField}
            className="flex items-start gap-2.5 text-[14px] leading-snug text-muted"
          >
            <input
              type="checkbox"
              checked={acceptedOffer}
              onChange={(e) => {
                setAcceptedOffer(e.target.checked);
                clearError('offer');
              }}
              disabled={isPending}
              aria-invalid={!!errors.offer}
              aria-describedby={errors.offer ? 'offer-error' : undefined}
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-accent',
                errors.offer && 'rounded-[3px] outline outline-2 outline-offset-2 outline-danger',
              )}
            />
            <span>
              Я принимаю{' '}
              <a
                href={OFFER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-accent hover:underline"
              >
                Публичную оферту
              </a>{' '}
              и{' '}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-accent hover:underline"
              >
                Политику конфиденциальности
              </a>
            </span>
          </motion.label>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {isSignUp && errors.offer && (
          <motion.div key="offer-error" id="offer-error" role="alert" {...collapseField} className="-mt-2 pl-[26px] text-[13px] text-danger">
            {errors.offer}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {backendError && !backendField && (
          <motion.div
            key="error"
            {...collapseField}
            className="rounded-xl bg-danger/15 px-3.5 py-2.5 text-[14px] text-danger"
          >
            {backendError}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div layout transition={layoutTransition}>
        <Button
          type="submit"
          size="lg"
          fullWidth
          className="mt-2"
          disabled={isPending}
        >
          {isPending ? '…' : t(isSignUp ? 'auth.signUp.submit' : 'auth.signIn.submit')}
        </Button>
      </motion.div>

      <motion.div layout transition={layoutTransition} className="mt-2 text-center text-[14px] text-muted">
        {t(isSignUp ? 'auth.signUp.hasAccount' : 'auth.signIn.noAccount')}{' '}
        <button type="button" onClick={switchMode} className="font-medium text-accent hover:underline">
          {t(isSignUp ? 'auth.signUp.switchToSignIn' : 'auth.signIn.switchToSignUp')}
        </button>
      </motion.div>
    </form>
  );
}
